<template>
  <v-card class="mx-auto ticket-card" outlined elevation="3">
    <v-toolbar flat dark class="ticket-toolbar">
      <v-toolbar-title class="font-weight-medium">
        <v-icon left>mdi-lifebuoy</v-icon>
        Support Ticket
      </v-toolbar-title>
      <v-spacer />
      <v-btn small text color="white" @click="onReset" :disabled="submitting">
        <v-icon left small>mdi-refresh</v-icon>Reset
      </v-btn>
    </v-toolbar>

    <v-progress-linear
      v-if="submitting"
      indeterminate
      color="primary"
      height="3"
      class="rounded-0"
    />

    <v-card-text class="pt-6 pb-2">
      <!-- Animated alerts -->
      <transition name="fade">
        <v-alert
          v-if="successMsg"
          type="success"
          text
          prominent
          class="mb-4"
          border="left"
        >
          <v-icon left color="success">mdi-check-circle</v-icon>
          {{ successMsg }}
        </v-alert>
      </transition>

      <transition name="fade">
        <v-alert
          v-if="errorMsg"
          type="error"
          text
          prominent
          class="mb-4"
          border="left"
        >
          <v-icon left color="error">mdi-alert</v-icon>
          {{ errorMsg }}
        </v-alert>
      </transition>

      <v-form
        ref="form"
        v-model="isValid"
        lazy-validation
        @submit.prevent="onSubmit"
      >
        <!-- Requester Info -->
        <v-sheet class="section-block" elevation="0">
          <div class="section-title">
            <v-icon left color="primary">mdi-account</v-icon>
            Requester Information
          </div>
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.name"
                label="Name"
                outlined
                dense
                :rules="[rules.required]"
                :disabled="submitting"
                required
                prepend-inner-icon="mdi-account"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.email"
                label="Email"
                type="email"
                outlined
                dense
                :rules="[rules.required, rules.email]"
                :disabled="submitting"
                required
                prepend-inner-icon="mdi-email"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.mobileno"
                label="Mobile No"
                type="tel"
                outlined
                dense
                :rules="[rules.required, rules.phone]"
                :disabled="submitting"
                required
                hint="Enter 10–15 digits"
                persistent-hint
                prepend-inner-icon="mdi-phone"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.orderNo"
                label="Order No"
                outlined
                dense
                :disabled="submitting"
                required
                prepend-inner-icon="mdi-receipt"
              />
              <!-- :rules="[rules.required]" -->
            </v-col>
          </v-row>
        </v-sheet>

        <!-- Ticket Details -->
        <v-divider class="my-6" />

        <v-sheet class="section-block" elevation="0">
          <div class="section-title">
            <v-icon left color="primary">mdi-file-document</v-icon>
            Ticket Details
          </div>
          <v-row>
            <v-col cols="12" sm="6">
              <v-select
                v-model="form.ticketType"
                :items="ticketTypes"
                label="Ticket Type"
                outlined
                dense
                :rules="[rules.required]"
                :disabled="submitting"
                required
                prepend-inner-icon="mdi-format-list-bulleted"
              />
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="form.description"
                label="Description"
                auto-grow
                outlined
                rows="3"
                counter="500"
                :hint="descHint"
                persistent-hint
                :rules="[rules.required, rules.minDesc]"
                :disabled="submitting"
                required
                prepend-inner-icon="mdi-comment-text"
              />
              <div class="caption text-right grey--text mt-n4 mb-2">
                {{ form.description.length }}/500
              </div>
            </v-col>
          </v-row>
        </v-sheet>

        <!-- Attachments -->
        <v-divider class="my-6" />

        <v-sheet class="section-block" elevation="0">
          <div class="section-title">
            <v-icon left color="primary">mdi-paperclip</v-icon>
            Attach Images (optional)
          </div>
          <v-row>
            <v-col cols="12">
              <v-file-input
                v-model="form.images"
                label="Choose up to 3 images (≤ 1 MB each)"
                outlined
                dense
                prepend-icon="mdi-image"
                accept="image/*"
                multiple
                chips
                small-chips
                counter
                show-size
                :disabled="submitting"
                :rules="[rules.maxFiles, rules.maxSize]"
                @change="onFilesChange"
              />
              <v-alert v-if="filesMsg" type="warning" text dense class="mt-2">
                {{ filesMsg }}
              </v-alert>
            </v-col>

            <v-col
              v-if="form.images && form.images.length"
              cols="12"
              class="pt-0"
            >
              <div class="preview-grid">
                <div
                  v-for="(img, idx) in form.images"
                  :key="idx"
                  class="preview-item"
                >
                  <v-img
                    :src="toObjectUrl(img)"
                    aspect-ratio="1"
                    class="rounded-lg"
                    contain
                  />
                  <div class="preview-meta">
                    <span class="text-truncate">{{ img.name }}</span>
                    <v-btn
                      icon
                      x-small
                      color="red"
                      @click="removeImage(idx)"
                      :disabled="submitting"
                    >
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>
              <v-btn
                small
                color="secondary"
                class="mt-2"
                outlined
                @click="clearImages"
                :disabled="submitting"
              >
                <v-icon left small>mdi-delete-sweep</v-icon>
                Clear Images
              </v-btn>
            </v-col>
          </v-row>
        </v-sheet>

        <!-- Verification -->
        <v-divider class="my-6" />

        <v-sheet class="section-block" elevation="0">
          <div class="section-title">
            <v-icon left color="primary">mdi-shield-key</v-icon>
            Verification
          </div>
          <v-row>
            <v-col cols="12" sm="6">
              <v-alert dense border="left" type="info" class="verification-box">
                <div class="d-flex align-center">
                  <v-chip
                    color="white"
                    outlined
                    label
                    class="mr-3 verification-chip"
                    :title="'Verification code: ' + randomCode"
                  >
                    {{ randomCode }}
                  </v-chip>
                  <v-btn
                    small
                    color="white"
                    outlined
                    @click="generateRandomCode"
                    :disabled="submitting"
                  >
                    <v-icon left small>mdi-refresh</v-icon>
                    Refresh
                  </v-btn>
                </div>
              </v-alert>
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.verifyCode"
                label="Enter Verification Code"
                outlined
                dense
                :rules="[rules.required, matchCodeRule]"
                :disabled="submitting"
                required
                autocomplete="off"
                prepend-inner-icon="mdi-shield-check"
              />
            </v-col>
          </v-row>
        </v-sheet>

        <!-- Submit -->
        <v-divider class="my-6" />

        <v-row>
          <v-col cols="12" class="d-flex justify-end">
            <v-btn
              color="primary"
              large
              :loading="submitting"
              :disabled="submitting"
              class="px-8"
              @click="onSubmit"
            >
              <v-icon left>mdi-send</v-icon>
              Submit Ticket
            </v-btn>
            <v-btn
              class="ml-3"
              large
              outlined
              :disabled="submitting"
              @click="onReset"
            >
              <v-icon left>mdi-backspace</v-icon>
              Reset
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import axios from "axios";
import { VUE_APP_BASEURL } from "../utils/api";

export default {
  name: "Ticket",
  props: {
    submitUrl: {
      type: String,
      default: () => VUE_APP_BASEURL + "ticket",
    },
    filesFieldName: {
      type: String,
      default: "images[]",
    },
  },
  data() {
    return {
      isValid: false,
      submitting: false,
      successMsg: "",
      errorMsg: "",
      filesMsg: "",
      randomCode: "",
      ticketTypes: ["Order", "Delivery", "Payment", "Complaint"],
      form: {
        name: "",
        email: "",
        mobileno: "",
        orderNo: "",
        ticketType: "",
        description: "",
        images: [],
        verifyCode: "",
      },
      rules: {
        required: (v) => !!v || "Required",
        email: (v) =>
          !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || "Invalid email",
        phone: (v) =>
          !v || /^[0-9+\-\s]{10,15}$/.test(v) || "Enter 10–15 digits",
        minDesc: (v) => !v || v.trim().length >= 10 || "Min 10 characters",
        maxFiles: (files) =>
          !files || files.length <= 3 || "Max 3 images allowed",
        maxSize: (files) =>
          !files ||
          Array.from(files).every((f) => f.size <= 1024 * 1024) ||
          "Each image must be ≤ 1 MB",
      },
    };
  },
  computed: {
    descHint() {
      if (!this.form.description) return "Explain the issue clearly.";
      if (this.form.description.length < 10)
        return "Add more details (≥10 chars).";
      if (this.form.description.length < 50)
        return "More detail helps faster resolution.";
      return "Looks good.";
    },
  },
  created() {
    this.generateRandomCode();
  },
  methods: {
    generateRandomCode() {
      this.randomCode = Math.floor(100000 + Math.random() * 900000).toString();
      this.form.verifyCode = "";
    },
    matchCodeRule(value) {
      return value === this.randomCode || "Code does not match";
    },
    toObjectUrl(file) {
      return URL.createObjectURL(file);
    },
    removeImage(index) {
      this.form.images.splice(index, 1);
      this.onFilesChange(this.form.images);
    },
    clearImages() {
      this.form.images = [];
      this.filesMsg = "";
    },
    onFilesChange(files) {
      this.filesMsg = "";
      let arr = Array.isArray(files) ? files.slice() : files ? [files] : [];
      let trimmed = arr.slice(0, 3);
      const droppedCount = arr.length - trimmed.length;
      const oversize = trimmed.filter((f) => f.size > 1024 * 1024);
      if (oversize.length) {
        trimmed = trimmed.filter((f) => f.size <= 1024 * 1024);
      }
      const msgs = [];
      if (droppedCount > 0)
        msgs.push(`Removed ${droppedCount} extra file(s) (max 3).`);
      if (oversize.length > 0)
        msgs.push(`${oversize.length} file(s) exceeded 1 MB and were removed.`);
      this.filesMsg = msgs.join(" ");
      this.form.images = trimmed;
    },
    async onSubmit() {
      this.successMsg = "";
      this.errorMsg = "";
      this.filesMsg = this.filesMsg || "";

      const ok = await this.$refs.form.validate();
      if (!ok) return;

      if (this.form.verifyCode !== this.randomCode) {
        this.errorMsg = "Verification code mismatch. Please try again.";
        this.generateRandomCode();
        return;
      }

      this.submitting = true;
      try {
        const fd = new FormData();
        fd.append("name", this.form.name);
        fd.append("email", this.form.email);
        fd.append("mobile_no", this.form.mobileno);
        fd.append("order_no", this.form.orderNo);
        fd.append("ticket_type", this.form.ticketType);
        fd.append("description", this.form.description);
        fd.append("verify_code", this.form.verifyCode);

        if (Array.isArray(this.form.images)) {
          this.form.images.forEach((file) => {
            fd.append(this.filesFieldName, file);
          });
        }

        const res = await axios.post(this.submitUrl, fd);

        this.successMsg =
          res?.data?.message || "Ticket submitted successfully.";
        this.$emit("submitted", {
          ok: true,
          payload: Object.fromEntries(fd),
          response: res.data,
        });
        this.onReset();
      } catch (err) {
        const apiMsg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message;
        this.errorMsg = apiMsg || "Failed to submit ticket. Please try again.";
        this.$emit("submitted", { ok: false, error: err });
      } finally {
        this.submitting = false;
      }
    },
    onReset() {
      this.$refs.form?.reset();
      this.$refs.form?.resetValidation();
      this.filesMsg = "";
      this.form.images = [];
      this.form.description = "";
      this.generateRandomCode();
      this.successMsg = "";
      this.errorMsg = "";
    },
  },
};
</script>

<style scoped>
.ticket-card {
  max-width: 960px;
  border-radius: 16px;
  overflow: hidden;
}

.ticket-toolbar {
  background: linear-gradient(135deg, #2e7d32, #1b5e20);
}

.section-block {
  padding: 4px 4px 0;
}

.section-title {
  font-weight: 600;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  color: #424242;
}

.verification-box {
  background: rgba(33, 150, 243, 0.06) !important;
}

.verification-chip {
  font-weight: 600;
  letter-spacing: 1px;
  font-size: 0.9rem;
}

.preview-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 4px;
}

.preview-item {
  width: 110px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.preview-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11px;
  background: #fafafa;
  padding: 4px 6px;
  border-radius: 6px;
  box-shadow: inset 0 0 0 1px #e0e0e0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 600px) {
  .ticket-card {
    border-radius: 0;
  }
  .preview-item {
    width: 30%;
  }
}
</style>
